/**
 * Lens color picker
 *
 * Web Component powering the lens color side drawer on the product page
 * (see snippets/lens-color-picker.liquid).
 *
 * Reuses the theme's existing 'side-drawer' custom element (defined in main.js,
 * loaded globally) for the drawer's open/close, overlay, focus trap, Escape key
 * and aria-hidden handling - no drawer/focus-trap logic is duplicated here.
 *
 * Reuses the theme's existing AJAX add-to-cart flow ('product-form' in main.js,
 * window.theme.routes) for the standard case (no price supplement). Only the
 * surcharge case is handled here, since it adds the frame and up to two
 * supplements in one request. A failed request is never retried as a frame-only add.
 *
 * Colors and finishes are driven entirely by two products managed in the
 * admin (see snippets/lens-color-picker.liquid): one with a variant per lens
 * color, one with a variant per finish. Variant titles, prices and
 * availability are rendered server-side; a page is a snapshot and Shopify
 * determines the final cart price. Each option radio carries
 * its supplement variant id (data-lens-variant-id), added to the cart as a
 * separate line item alongside the frame.
 *
 * -----------------------------------------------------------------------
 * ADMIN SETUP REQUIRED
 * -----------------------------------------------------------------------
 * Two products must exist in the admin. Confirm the Gaia/Shopify mapping
 * before editing them; the historical import CSV is not in this repository:
 *   1. "Couleur de verre" - handle: supplement-verre-couleur - one variant
 *      per lens color (option name "Couleur"), each with its own price.
 *   2. "Option de verre" - handle: supplement-verre-option - one variant per
 *      finish (option name "Option"), e.g. Dégradé / Transition / Polarisé.
 * Both must be Active on the Online Store channel, with purchasable variants.
 * Inventory policy for these services requires separate business approval;
 * do not invent inventory. Recommended: exclude them from search,
 * collections and navigation - they are only ever added programmatically.
 * To manage the offer, simply edit those products: rename variants, change
 * prices, add or disable variants - the drawer follows automatically.
 * Optional: set an image on a color variant to use it as the drawer swatch
 * (otherwise the hex from the block's "Swatch colors" setting applies).
 * -----------------------------------------------------------------------
 */
/* global theme */

if (!customElements.get('lens-color-picker')) {
  class LensColorPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.productId = this.form ? this.form.dataset.productId : null;
      this.drawer = this.querySelector('side-drawer');
      this.trigger = this.querySelector('[data-role="trigger"]');
      this.triggerSwatch = this.querySelector('[data-role="trigger-swatch"]');
      this.triggerText = this.querySelector('[data-role="trigger-text"]');
      this.valueInput = this.querySelector('.lens-color-picker__value-input');
      this.finishInput = this.querySelector('.lens-color-picker__finish-input');
      this.radios = Array.from(this.querySelectorAll('.lens-color-picker__radio'));
      this.finishRadios = Array.from(this.querySelectorAll('.lens-color-picker__finish-radio'));
      this.switcherTrack = this.querySelector('[data-role="switcher-track"]');
      this.total = this.querySelector('[data-role="total"]');
      this.totalAmount = this.querySelector('[data-role="total-amount"]');

      this.propertyName = this.dataset.propertyName;
      this.finishPropertyName = this.dataset.finishPropertyName;
      this.isAdding = false;
      // 'fr' locale on purpose: prices in the picker are always displayed
      // French-style ("10,00 €"), regardless of the shop's currency format.
      this.moneyFormatter = new Intl.NumberFormat('fr', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR'
      });

      if (this.trigger && this.drawer) {
        this.trigger.addEventListener('click', () => this.drawer.open(this.trigger));
      }

      this.addEventListener('change', this.handleOptionChange.bind(this));

      // Intercept 'submit' at document level in the capture phase (before the
      // theme's 'product-form' AJAX handler bound directly on the form). This
      // survives the variant picker's [data-dynamic-variant-content] element
      // replacement (which recreates the add-to-cart button) and also covers
      // the sticky add-to-cart bar, which submits its own separate form.
      this.submitHandler = this.handleFormSubmit.bind(this);
      document.addEventListener('submit', this.submitHandler, true);

      this.formatFinishPriceLabels();
      this.updateSwitcherThumb();
      this.updateColorPrices();
      this.updateTotal();
    }

    disconnectedCallback() {
      if (this.submitHandler) {
        document.removeEventListener('submit', this.submitHandler, true);
      }
    }

    /**
     * Handles selection of a lens color or finish option inside the drawer.
     * The drawer stays open so the customer can pick a finish and confirm.
     * @param {Event} evt - Change event.
     */
    handleOptionChange(evt) {
      if (evt.target.classList.contains('lens-color-picker__radio')) {
        this.handleColorChange(evt.target);
      } else if (evt.target.classList.contains('lens-color-picker__finish-radio')) {
        this.handleFinishChange(evt.target);
      }
    }

    /**
     * Handles selection of a lens color: updates the cart property input and
     * the trigger button label.
     * @param {Element} radio - Selected color radio input.
     */
    handleColorChange(radio) {
      if (this.valueInput) this.valueInput.value = radio.value;
      if (this.triggerSwatch) this.triggerSwatch.style.setProperty('--swatch-background-color', radio.dataset.lensColor || 'transparent');

      this.updateTotal();
      this.updateTriggerText();
    }

    /**
     * Handles selection of a lens type in the switcher (independent from the
     * color choice - the same colors are available whatever the lens type).
     * @param {Element} radio - Selected finish radio input.
     */
    handleFinishChange(radio) {
      this.syncFinishInput(radio);
      this.updateSwitcherThumb();
      this.updateColorPrices();
      this.updateTotal();
      this.updateTriggerText();
    }

    /**
     * Rewrites the server-rendered lens type prices (shop money format, e.g.
     * "+€10,00") into the French format used everywhere in the picker.
     */
    formatFinishPriceLabels() {
      this.finishRadios.forEach((radio) => {
        const price = Number(radio.dataset.lensPrice) || 0;
        if (price === 0) return;
        const label = `+${this.moneyFormatter.format(price / 100)}`;
        radio.dataset.lensPriceLabel = label;
        const tab = radio.closest('.lens-color-picker__tab');
        const priceEl = tab && tab.querySelector('.lens-color-picker__tab-price');
        if (priceEl) priceEl.textContent = label;
      });
    }

    /**
     * Updates the running total of the lens choices (color + lens type)
     * displayed above the confirm button.
     */
    updateTotal() {
      if (!this.totalAmount) return;
      const color = this.getSelectedRadio();
      const total = (color ? this.getColorBasePrice(color) : 0) + this.getFinishPrice();
      this.totalAmount.textContent = total === 0
        ? (this.total && this.total.dataset.includedText) || ''
        : `+${this.moneyFormatter.format(total / 100)}`;
    }

    /**
     * Slides the switcher thumb under the currently checked lens type.
     */
    updateSwitcherThumb() {
      if (!this.switcherTrack) return;
      const index = this.finishRadios.findIndex((radio) => radio.checked);
      this.switcherTrack.style.setProperty('--lcp-index', Math.max(0, index));
    }

    /**
     * @returns {number} Price (in cents) of the currently checked lens type.
     */
    getFinishPrice() {
      const checked = this.finishRadios.find((radio) => radio.checked);
      return checked ? Number(checked.dataset.lensPrice) || 0 : 0;
    }

    /**
     * Base price (in cents) of a color option given the current lens type.
     * The standard (included) lens becomes a paid lens when a lens type is
     * selected - a Dégradé/Transition/Polarisant lens must be purchased -
     * charged through its dedicated variant (data-lens-paid-price).
     * @param {Element} radio - Lens color radio input.
     * @returns {number} Base price in cents.
     */
    getColorBasePrice(radio) {
      const base = Number(radio.dataset.lensPrice) || 0;
      if (!radio.dataset.lensVariantId && this.getSelectedFinishRadio()) return Number(radio.dataset.lensPaidPrice) || 0;
      return base;
    }

    /**
     * Builds the price label for a color option, combining the color price
     * with the currently selected lens type supplement (e.g. 39 € + 10 € =
     * "+49,00 €"). Falls back to the server-rendered label (e.g. "Inclus")
     * when nothing is charged.
     * @param {Element} radio - Lens color radio input.
     * @returns {string} Price label to display.
     */
    getColorPriceLabel(radio) {
      if (!radio.dataset.lensVariantId && this.getSelectedFinishRadio() && !radio.dataset.lensPaidVariantId) {
        return window.lensCartGuard ? window.lensCartGuard.getErrorMessage('unavailable') : '';
      }
      const total = this.getColorBasePrice(radio) + this.getFinishPrice();
      if (total === 0) return radio.dataset.lensPriceLabel || '';
      return `+${this.moneyFormatter.format(total / 100)}`;
    }

    /**
     * Refreshes the price displayed next to each color to reflect the
     * selected lens type (color price + type supplement).
     */
    updateColorPrices() {
      this.radios.forEach((radio) => {
        const label = radio.closest('.lens-color-picker__option');
        const priceEl = label && label.querySelector('.lens-color-picker__option-price');
        if (priceEl) priceEl.textContent = this.getColorPriceLabel(radio);
      });
    }

    /**
     * Keeps the hidden finish property input in sync (disabled when no finish
     * is chosen, so no empty property reaches the cart).
     * @param {Element} [radio] - Selected finish radio input.
     */
    syncFinishInput(radio) {
      if (!this.finishInput) return;
      if (radio && radio.value) {
        this.finishInput.disabled = false;
        this.finishInput.value = radio.value;
      } else {
        this.finishInput.value = '';
        this.finishInput.disabled = true;
      }
    }

    /**
     * Updates the trigger button label with the current color/finish choice.
     */
    updateTriggerText() {
      if (!this.triggerText) return;
      const color = this.getSelectedRadio();
      if (!color) return;

      const parts = [color.value];
      const finish = this.getSelectedFinishRadio();
      if (finish) parts.push(finish.value);
      parts.push(this.getColorPriceLabel(color));

      this.triggerText.textContent = parts.join(' \u00B7 ');
    }

    /**
     * @returns {Element|undefined} Currently checked lens color radio.
     */
    getSelectedRadio() {
      return this.radios.find((radio) => radio.checked);
    }

    /**
     * @returns {Element|undefined} Checked finish radio, if it is a real
     * finish (the default "none" option returns undefined).
     */
    getSelectedFinishRadio() {
      return this.finishRadios.find((radio) => radio.checked && radio.value);
    }

    /**
     * Handles submission of any add-to-cart form for this product (main
     * product form or the sticky add-to-cart bar's separate form).
     * Only intercepts the flow when something must be charged (priced lens
     * color and/or lens type) - the standard lens with the default type
     * falls through to the theme's existing AJAX add-to-cart handling.
     * @param {Event} evt - Submit event (captured at document level).
     */
    handleFormSubmit(evt) {
      const form = evt.target;
      if (evt.defaultPrevented) return;
      if (!(form instanceof HTMLFormElement) || !form.matches('.js-product-form')) return;
      if (!this.productId || form.dataset.productId !== this.productId) return;
      if (!form.querySelector('[name="add"]')) return;

      if (this.isAdding) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }

      const selected = this.getSelectedRadio();
      if (!selected) return;

      // The main form carries the lens color/finish via the form-associated
      // hidden inputs; other forms (e.g. sticky bar) need them injected at
      // submit time, as they get recreated on variant change.
      if (form !== this.form) {
        this.ensurePropertyInput(form, this.propertyName, selected.value, 'data-lens-color-property');
        const finish = this.getSelectedFinishRadio();
        this.ensurePropertyInput(form, this.finishPropertyName, finish ? finish.value : '', 'data-lens-finish-property');
      }

      // Standard lens with the default lens type: nothing to charge, fall
      // through to the theme's regular add-to-cart flow. Any supplement
      // (color and/or lens type) triggers the custom multi-item add.
      const finishSelected = this.getSelectedFinishRadio();
      if (!selected.dataset.lensVariantId && !finishSelected && !(Number(selected.dataset.lensPrice) > 0)) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.addFrameAndSupplementToCart(selected, form);
    }

    /**
     * Ensures the given product form carries a 'properties[...]' hidden input
     * with the provided value.
     * @param {HTMLFormElement} form - Product form to update.
     * @param {string} propertyName - Line item property name.
     * @param {string} value - Property value.
     * @param {string} marker - Data attribute identifying the input.
     */
    ensurePropertyInput(form, propertyName, value, marker) {
      let input = form.querySelector(`input[${marker}]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${propertyName}]`;
        input.setAttribute(marker, '');
        form.appendChild(input);
      }
      input.value = value;
      input.disabled = !value;
    }

    /**
     * Adds the frame variant and the lens color supplement to the cart in a
     * single request, then verifies the group before redirecting to the cart.
     * Errors leave the selection visible and never submit the frame alone.
     * @param {Element} selectedRadio - Selected lens color radio input.
     * @param {HTMLFormElement} form - The submitted product form.
     */
    async addFrameAndSupplementToCart(selectedRadio, form) {
      if (this.isAdding) return;
      const addToCartBtn = form.querySelector('[name="add"]');
      if (addToCartBtn && (addToCartBtn.disabled || addToCartBtn.getAttribute('aria-disabled') === 'true')) return;

      const productForm = this.form && this.form.closest('product-form');
      if (productForm && typeof productForm.validate === 'function' && !productForm.validate()) return;
      if (this.form && !this.form.reportValidity()) return;

      this.isAdding = true;
      this.setCartError(form);
      if (addToCartBtn) {
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('is-loading');
      }

      try {
        const cartGuard = window.lensCartGuard;
        if (!cartGuard || !window.crypto || typeof window.crypto.randomUUID !== 'function') throw new Error('Lens cart guard unavailable.');
        const finishRadio = this.getSelectedFinishRadio();
        const finishVariantId = finishRadio ? finishRadio.dataset.lensVariantId : null;

        // A colored lens is charged through its own variant; the standard
        // lens is charged through its paid variant (e.g. "Incolore") as soon
        // as a lens type is selected, since that lens must be purchased.
        const supplementVariantId = selectedRadio.dataset.lensVariantId
          || (finishVariantId && selectedRadio.dataset.lensPaidVariantId)
          || null;

        if (!supplementVariantId && !finishVariantId) throw new Error('Lens color picker: no supplement variant to add.');
        if (finishRadio && (!finishVariantId || !supplementVariantId)) throw new Error('Lens color picker: incomplete lens configuration.');

        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        const quantity = Number(formData.get('quantity') || 1);
        const lensName = selectedRadio.value;

        const variantIds = [mainVariantId, supplementVariantId, finishVariantId].filter((id) => id !== null);
        if (!mainVariantId || variantIds.some((id) => !/^[1-9]\d*$/.test(id)) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Lens color picker: invalid variant or quantity.');

        const frameProperties = {};
        const propertyData = this.form && form !== this.form ? [new FormData(this.form), formData] : [formData];
        propertyData.forEach((data) => {
          data.forEach((value, name) => {
            const match = name.match(/^properties\[(.+)\]$/);
            if (!match || match[1].startsWith('_lcp_') || match[1] === '_monture') return;
            if (typeof value !== 'string') {
              if (value.size) throw new Error('Lens color picker: file properties require a dedicated upload flow.');
              return;
            }
            Object.defineProperty(frameProperties, match[1], { value, enumerable: true, writable: true, configurable: true });
          });
        });
        frameProperties[this.propertyName] = lensName;
        delete frameProperties[this.finishPropertyName];
        if (finishRadio) frameProperties[this.finishPropertyName] = finishRadio.value;

        const groupId = window.crypto.randomUUID();
        frameProperties._lcp_group = groupId;
        frameProperties._lcp_components = JSON.stringify([supplementVariantId, finishVariantId].filter(Boolean));

        const items = [
          {
            id: mainVariantId,
            quantity,
            properties: frameProperties
          }
        ];

        const sellingPlan = formData.get('selling_plan') || (this.form && new FormData(this.form).get('selling_plan'));
        if (sellingPlan) {
          if (!/^[1-9]\d*$/.test(sellingPlan)) throw new Error('Lens color picker: invalid selling plan.');
          items[0].selling_plan = sellingPlan;
        }

        if (supplementVariantId) {
          items.push({
            id: supplementVariantId,
            quantity,
            properties: {
              [this.propertyName]: lensName,
              // Underscore prefix: hidden in the cart and at checkout, only
              // used to tie the supplement line to its frame.
              _monture: mainVariantId,
              _lcp_group: groupId
            }
          });
        }

        if (finishVariantId) {
          items.push({
            id: finishVariantId,
            quantity,
            properties: {
              [this.finishPropertyName]: finishRadio.value,
              _monture: mainVariantId,
              _lcp_group: groupId
            }
          });
        }

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || `Lens color picker: cart add failed (${response.status}).`);
        }

        const cart = await cartGuard.getCart();
        if (!cartGuard.isGroupComplete(cart, groupId)) throw new Error('Lens color picker: added group is incomplete.');
        window.location.href = theme.routes.cart;
      } catch (error) {
        this.fallbackToStandardSubmit(form);
      } finally {
        this.isAdding = false;
        if (addToCartBtn) {
          addToCartBtn.classList.remove('is-loading');
          addToCartBtn.removeAttribute('aria-disabled');
        }
      }
    }

    /**
     * Legacy entry point retained for compatibility. An uncertain add must
     * NEVER fall back to a frame-only submission or retry automatically.
     * @param {HTMLFormElement} form - The product form to submit.
     */
    fallbackToStandardSubmit(form) {
      const message = window.lensCartGuard
        ? window.lensCartGuard.getErrorMessage('add')
        : 'Impossible de confirmer la personnalisation. Vérifiez votre panier avant de réessayer.';
      this.setCartError(form, message);
      this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: message } }));
      this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    }

    /**
     * Uses the native form's error area, including the sticky bar. Messages
     * are text only; no server response or customer property is injected as HTML.
     */
    setCartError(form, message = '') {
      const container = form.closest('sticky-atc, product-form') || form;
      let error = container.querySelector('.js-form-error');
      if (!error && message) {
        error = document.createElement('p');
        error.className = 'js-form-error';
        error.setAttribute('role', 'alert');
        container.appendChild(error);
      }
      if (error) {
        error.hidden = !message;
        error.textContent = message;
      }
    }
  }

  customElements.define('lens-color-picker', LensColorPicker);
}
